import { chatPartnerUid, isSelfChat, unreadCountFor } from '../../../services/chat/chatApi';

export const SELF_NOTE_EMPTY = 'Write a note to yourself.';

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const d = value instanceof Date ? value : new Date(value);
  const n = d.getTime();
  return Number.isFinite(n) ? n : 0;
}

export function lastMessagePreview(chat, uid) {
  const text = String(chat?.lastMessage?.text || '').trim();
  if (!text) return 'No messages yet';
  if (isSelfChat(chat, uid)) return text;
  if (chat.lastMessage.senderUid === uid) return `You: ${text}`;
  return text;
}

export function selfNoteMatchesQuery(query, profile) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  if ('you'.includes(q)) return true;
  const names = [profile?.displayName, profile?.username]
    .filter(Boolean)
    .map((name) => String(name).toLowerCase());
  return names.some((name) => name.includes(q));
}

export function formatChatTime(value, now = Date.now()) {
  const ms = toMillis(value);
  if (!ms) return '';
  const diff = Math.max(0, now - ms);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function filterChatsByQuery(chats, partners, uid, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return chats || [];
  return (chats || []).filter((chat) => {
    const partnerUid = chatPartnerUid(chat, uid);
    const name = partnerLabel(partners?.[partnerUid], partnerUid).toLowerCase();
    return name.includes(q);
  });
}

export function partnerLabel(profile, fallbackUid = '') {
  if (profile?.displayName) return profile.displayName;
  if (profile?.username) return `@${profile.username}`;
  return fallbackUid ? 'MapToBe user' : 'Someone';
}

export function isSameChatDay(a, b) {
  const am = toMillis(a);
  const bm = toMillis(b);
  if (!am || !bm) return false;
  const da = new Date(am);
  const db = new Date(bm);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

export function formatChatDayLabel(value) {
  const ms = toMillis(value);
  if (!ms) return '';
  return new Date(ms).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function unreadInboxSummary(chats, uid) {
  const unreadChats = (chats || []).filter((c) => unreadCountFor(c, uid) > 0);
  const unreadTotal = unreadChats.reduce((n, c) => n + unreadCountFor(c, uid), 0);
  return { unreadChats, unreadTotal };
}

export function chatBellItems(unreadChats, partners, uid) {
  return (unreadChats || []).map((chat) => {
    const fromUid = chatPartnerUid(chat, uid);
    const p = fromUid ? partners?.[fromUid] : null;
    return {
      id: `chat_${chat.id}`,
      kind: 'chat_message',
      chatId: chat.id,
      fromUid,
      fromLabel:
        p?.displayName ||
        (p?.username ? `@${p.username}` : '') ||
        'Someone',
      fromPhotoURL: p?.photoURL || null,
      fromUsername: p?.username || null,
      preview: String(chat.lastMessage?.text || 'New message').trim() || 'New message',
      createdAt: chat.lastMessage?.createdAt || chat.updatedAt,
    };
  });
}
